import { Fragment } from 'react';

// Third-party
import CardHeader from '@mui/material/CardHeader';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import SvgIcon from '@mui/material/SvgIcon';

// Assets
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { ReactComponent as TrashIcon } from '../../../../static/icons/ic_trash.svg';
import { ReactComponent as EditIcon } from '../../../../static/icons/ic_edit.svg';

// Hooks
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';

// Redux-toolkit
import { getSelectedTasklist } from '../../../../store/tasklists/selectors';
import { tasklistsActions } from '../../../../store/tasklists/slice';
import { commonActions } from '../../../../store/common/slice';
import { useTasklistPermissions } from '../../Shared/hooks/useTasklistPermissions';
import { useFilterTasklists } from '../hooks';

const TasklistBlock = () => {
  const selectedTasklistTemplate = useAppSelector(getSelectedTasklist);
  const dispatch = useAppDispatch();
  const { tasklistPermissions } = useTasklistPermissions();
  const { getTasklistsByFilters } = useFilterTasklists();

  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const { t } = useTranslation('tasklists');

  const handleEdit = () => {
    if (getTasklistsByFilters?.length === 1) {
      dispatch(tasklistsActions.setSelectedTasklistForAction(getTasklistsByFilters[0]));
    } else dispatch(tasklistsActions.setSelectedTasklistForAction(selectedTasklistTemplate));
    dispatch(commonActions.setIsOpenPagesNav(true));
  };

  const handleDelete = () => {
    dispatch(tasklistsActions.setSelectedTasklistForAction(selectedTasklistTemplate));
    dispatch(tasklistsActions.setIsOpenDeleteDialog(true));
  };

  return (
    <Card sx={{
      mt: 3,
      height: '46vh',
      overflow: 'auto',
      position: 'relative',
      ...(isMobile && {
        backgroundColor: 'unset',
        height: 'auto',
        minHeight: '49vh',
      }),
    }}
    >
      {selectedTasklistTemplate.id ? (
        <Box>
          <CardHeader
            sx={{
              px: isMobile ? 1.5 : 3,
            }}
            title={selectedTasklistTemplate.title}
            subheader={selectedTasklistTemplate.description}
            subheaderTypographyProps={{
              pt: 0.5,
              variant: 'body2',
            }}
            {...(tasklistPermissions.isAllowManage && {
              action: (
                <Stack marginTop={1} direction="row" spacing={2}>
                  <SvgIcon
                    className="cursor-pointer"
                    onClick={handleEdit}
                    fontSize="small"
                    inheritViewBox
                    component={EditIcon}
                  />
                  <SvgIcon
                    className="cursor-pointer"
                    onClick={handleDelete}
                    fontSize="small"
                    inheritViewBox
                    component={TrashIcon}
                  />
                </Stack>
              ),
            })}
          />
          <Divider sx={{ borderStyle: 'dashed', pt: 2 }} />
          <Box sx={{ px: isMobile ? 1.5 : 3, py: 1 }}>
            {selectedTasklistTemplate.tasks?.map((task) => (
              <Fragment key={task.taskName}>
                <Stack direction="row" justifyContent="space-between" sx={{ py: 2 }}>
                  <Typography variant="body2">{task.taskName}</Typography>
                </Stack>
                <Divider sx={{ borderStyle: 'dashed' }} />
              </Fragment>
            ))}
          </Box>
        </Box>
      ) : (
        <CustomNoRowsOverlay
          iconHeight="175"
          iconWidth="175"
          textWidth={400}
          text={t('noTasklists')}
        />
      )}
    </Card>
  );
};

export {
  TasklistBlock,
};
